// Security and sessions (AUTH-01/02): passkeys, TOTP enrolment (the otpauth URI, and so the secret,
// is returned once), recovery codes shown once, and the session list with immediate revocation.
// Enrolling a second factor needs a recent MFA, so those calls go through the step-up dialog.
// The session list is shared with /account/sessions (SessionsPage).
import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { browserSupportsWebAuthn, startRegistration } from "@simplewebauthn/browser";
import { formatRelative, normalizeDigits } from "@ady/format";
import { t } from "@ady/i18n";
import {
  Badge,
  Banner,
  BusyRegion,
  Button,
  Dialog,
  ErrorState,
  Field,
  Input,
  SkeletonText,
  useToast,
} from "@ady/ui";
import { ApiError, api } from "../api/client";
import { errorMessage } from "../api/errors";
import { keys, useMe, useMfaFactors, useSessions } from "../api/queries";
import type { SessionInfo, TotpEnrollResponse } from "../api/types";
import { TID } from "../testids";
import { PageHeader } from "../shell/PageHeader";
import { PhoneCard } from "./PhoneCard";
import { stepUpErrorMessage, useStepUp } from "./StepUp";

function Card({ title, body, children }: { title: string; body: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-md border border-subtle bg-surface p-5">
      <h2 className="text-title-sm font-semibold">{title}</h2>
      <p className="text-md text-fg-secondary">{body}</p>
      {children}
    </section>
  );
}

/** The base32 secret inside an otpauth:// URI (shown as the manual setup key). */
export function secretFromUri(uri: string): string {
  const m = /[?&]secret=([^&]+)/i.exec(uri);
  return m?.[1] ? decodeURIComponent(m[1]) : "";
}

/** Shown once, right after the first factor is confirmed (10 single-use codes). */
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const toast = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.show({ tone: "success", title: t("security.recoveryCopied") });
    } catch {
      toast.show({ tone: "error", title: t("team.inviteDialog.copyFailed") });
    }
  };
  return (
    <div
      className="flex flex-col gap-3 rounded-sm border border-warning-border bg-warning-bg p-4"
      data-testid={TID.recoveryCodes}
    >
      <p className="text-md font-semibold">{t("security.recoveryTitle")}</p>
      <p className="text-sm text-fg">{t("security.recoveryBody")}</p>
      <ol className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
        {codes.map((c) => (
          <li key={c} className="ady-ledger text-md" data-testid={TID.recoveryCode}>
            {c}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-3">
        <Button icon="file" variant="secondary" size="md" onClick={copy}>
          {t("security.recoveryCopy")}
        </Button>
        <Button icon="check" size="md" onClick={onDone}>
          {t("security.recoveryDone")}
        </Button>
      </div>
    </div>
  );
}

function TotpCard({ onRecoveryCodes }: { onRecoveryCodes: (codes: string[]) => void }) {
  const me = useMe();
  const factors = useMfaFactors();
  const qc = useQueryClient();
  const toast = useToast();
  const stepUp = useStepUp();
  const [enroll, setEnroll] = useState<TotpEnrollResponse | null>(null);
  const [justEnabled, setJustEnabled] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const start = useMutation({
    mutationFn: () => stepUp(() => api.totpEnroll()),
    onSuccess: (res) => {
      setError(undefined);
      setEnroll(res);
    },
    onError: (e) => setError(stepUpErrorMessage(e) ?? undefined),
  });
  const confirm = useMutation({
    mutationFn: () => api.totpConfirm(code),
    onSuccess: async (res) => {
      setEnroll(null);
      setJustEnabled(true); // the server confirmed it; /me and /auth/mfa refetch below
      setCode("");
      setError(undefined);
      if (res?.recovery_codes?.length) onRecoveryCodes(res.recovery_codes);
      toast.show({ tone: "success", title: t("security.totpConfirmed") });
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.me }),
        qc.invalidateQueries({ queryKey: keys.mfa }),
      ]);
    },
    onError: (e) => setError(errorMessage(e)),
  });
  const enabled =
    justEnabled || Boolean(factors.data?.totp?.confirmed_at) || (me.data?.user.mfa.totp ?? false);
  return (
    <Card title={t("security.totpTitle")} body={t("security.totpBody")}>
      <div
        className="flex items-center gap-3"
        data-testid={TID.totpStatus}
        data-state={enabled ? "enabled" : "off"}
      >
        <Badge tone={enabled ? "success" : "neutral"}>
          {enabled ? t("security.totpEnabled") : t("security.totpOff")}
        </Badge>
      </div>
      {!enabled && !enroll && error ? <Banner kind="error" title={error} /> : null}
      {!enabled && !enroll ? (
        <div>
          <Button
            variant="secondary"
            onClick={() => start.mutate()}
            loading={start.isPending}
            data-testid={TID.totpStart}
          >
            {t("security.totpStart")}
          </Button>
        </div>
      ) : null}
      {enroll ? (
        <div className="flex flex-col gap-4 rounded-sm border border-subtle bg-sunken p-4">
          <p className="text-md font-medium">{t("security.totpScanTitle")}</p>
          <p className="text-sm text-fg-secondary">{t("security.totpScanBody")}</p>
          <dl className="grid gap-2 text-sm">
            <dt className="font-medium">{t("security.totpSecretLabel")}</dt>
            <dd className="ady-ledger text-md break-all" data-testid={TID.totpSecret}>
              {secretFromUri(enroll.otpauth_uri)}
            </dd>
            <dt className="font-medium">{t("security.totpUriLabel")}</dt>
            <dd className="break-all text-fg-secondary">{enroll.otpauth_uri}</dd>
          </dl>
          <form
            noValidate
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              confirm.mutate();
            }}
          >
            <Field label={t("security.totpConfirmLabel")} error={error}>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) =>
                  setCode(normalizeDigits(e.target.value).replace(/\D/g, "").slice(0, 6))
                }
                ledger
                data-testid={TID.totpCode}
              />
            </Field>
            <div>
              <Button
                icon="shieldCheck"
                type="submit"
                loading={confirm.isPending}
                data-testid={TID.totpConfirm}
              >
                {t("security.totpConfirm")}
              </Button>
            </div>
          </form>
        </div>
      ) : null}
    </Card>
  );
}

function PasskeyCard({ onRecoveryCodes }: { onRecoveryCodes: (codes: string[]) => void }) {
  const factors = useMfaFactors();
  const qc = useQueryClient();
  const toast = useToast();
  const stepUp = useStepUp();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setError(undefined);
    if (!browserSupportsWebAuthn()) return setError(t("auth.passkeyUnsupported"));
    setBusy(true);
    try {
      const res = await stepUp(async () => {
        const opts = await api.passkeyOptions("register");
        const response = await startRegistration({ optionsJSON: opts.options as never });
        return api.passkeyRegister(opts.challenge_id, response);
      });
      if (res?.recovery_codes?.length) onRecoveryCodes(res.recovery_codes);
      toast.show({ tone: "success", title: t("security.passkeyAdded") });
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.mfa }),
        qc.invalidateQueries({ queryKey: keys.me }),
      ]);
    } catch (err) {
      const msg =
        err instanceof Error && err.name === "NotAllowedError"
          ? t("auth.passkeyCancelled")
          : stepUpErrorMessage(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };
  const passkeys = factors.data?.passkeys ?? [];
  return (
    <Card title={t("security.passkeysTitle")} body={t("security.passkeysBody")}>
      {error ? <Banner kind="error" title={error} /> : null}
      {factors.isError ? (
        <Banner kind="error" title={t("security.passkeysLoadFailed")} />
      ) : factors.isPending ? null : passkeys.length === 0 ? (
        <p className="text-sm text-fg-tertiary">{t("security.noPasskeys")}</p>
      ) : (
        <ul aria-label={t("security.passkeyListLabel")} className="flex flex-col gap-2">
          {passkeys.map((k, i) => (
            <li
              key={k.id}
              data-testid={TID.passkeyItem}
              className="flex flex-col rounded-sm border border-subtle px-3 py-2"
            >
              <span className="text-md font-medium">{t("security.passkeyName", { n: i + 1 })}</span>
              <span className="text-sm text-fg-secondary">
                {[
                  k.created_at
                    ? t("security.passkeyAddedOn", { when: formatRelative(k.created_at) })
                    : null,
                  k.last_used_at
                    ? t("security.passkeyLastUsed", { when: formatRelative(k.last_used_at) })
                    : t("security.passkeyNeverUsed"),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div>
        <Button
          variant="secondary"
          icon="fingerprint"
          onClick={add}
          loading={busy}
          data-testid={TID.passkeyAdd}
        >
          {t("security.addPasskey")}
        </Button>
      </div>
    </Card>
  );
}

/** The signed-in devices with immediate revocation (Security page and /account/sessions). */
export function SessionsList() {
  const sessions = useSessions();
  const qc = useQueryClient();
  const toast = useToast();
  const stepUp = useStepUp();
  const [target, setTarget] = useState<SessionInfo | "all" | null>(null);
  const device = (s: SessionInfo) => s.device ?? t("security.unknownDevice");
  const revoke = useMutation({
    mutationFn: (s: SessionInfo | "all") =>
      stepUp(async () => {
        if (s === "all") await api.revokeOtherSessions();
        else await api.revokeSession(s.id);
      }),
    onSuccess: async (_d, s) => {
      setTarget(null);
      toast.show({
        tone: "success",
        title:
          s === "all" ? t("security.revokedAll") : t("security.revoked", { device: device(s) }),
      });
      await qc.invalidateQueries({ queryKey: keys.sessions });
    },
    onError: (e) => {
      const msg = stepUpErrorMessage(e);
      if (msg) toast.show({ tone: "error", title: msg });
    },
  });
  const rows = sessions.data?.data ?? [];
  const others = rows.filter((r) => !r.current);

  if (sessions.isError) {
    return (
      <ErrorState
        size="inline"
        data-testid={TID.errorState}
        body={errorMessage(sessions.error)}
        requestId={sessions.error instanceof ApiError ? sessions.error.requestId : undefined}
        onRetry={() => void sessions.refetch()}
      />
    );
  }
  return (
    <>
      <BusyRegion
        busy={sessions.isPending}
        skeleton={<SkeletonText lines={4} />}
        data-testid={sessions.isPending ? TID.loadingState : TID.sessionsTable}
      >
        <ul
          aria-label={t("security.sessionsListLabel")}
          className="flex flex-col divide-y divide-subtle rounded-md border border-subtle"
        >
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={TID.sessionRow}
              data-current={r.current || undefined}
              className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="flex flex-wrap items-center gap-2 text-md font-medium">
                  <span className="break-words">{device(r)}</span>
                  {r.current ? (
                    <Badge tone="action" data-testid={TID.sessionCurrent}>
                      {t("security.thisDevice")}
                    </Badge>
                  ) : null}
                </p>
                <p className="text-sm text-fg-secondary">
                  {[
                    r.last_seen_at
                      ? t("security.lastActive", { when: formatRelative(r.last_seen_at) })
                      : null,
                    r.created_at
                      ? t("security.signedInAt", { when: formatRelative(r.created_at) })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              {r.current ? null : (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setTarget(r)}
                  data-testid={TID.revokeSession(r.id)}
                >
                  {t("security.revoke")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </BusyRegion>
      {others.length > 0 ? (
        <div>
          <Button
            variant="secondary"
            onClick={() => setTarget("all")}
            data-testid={TID.revokeAllSessions}
          >
            {t("security.revokeAll")}
          </Button>
        </div>
      ) : sessions.isSuccess ? (
        <p className="text-sm text-fg-secondary">{t("security.noOtherSessions")}</p>
      ) : null}
      <Dialog
        open={target !== null}
        onClose={() => setTarget(null)}
        title={
          target === "all"
            ? t("security.revokeAll")
            : t("security.revokeConfirmTitle", { device: target ? device(target) : "" })
        }
        description={t("security.revokeConfirmBody")}
        confirm={{
          label: target === "all" ? t("security.revokeAll") : t("security.revoke"),
          variant: "danger",
          loading: revoke.isPending,
          onClick: () => target && revoke.mutate(target),
          "data-testid": TID.confirmDialogConfirm,
        }}
      />
    </>
  );
}

export function SecurityPage() {
  const [codes, setCodes] = useState<string[] | null>(null);
  return (
    <div className="flex max-w-3xl flex-col gap-6" data-testid={TID.securityPage}>
      <PageHeader title={t("security.title")} lead={t("security.lead")} />
      {codes ? <RecoveryCodes codes={codes} onDone={() => setCodes(null)} /> : null}
      <PasskeyCard onRecoveryCodes={setCodes} />
      <TotpCard onRecoveryCodes={setCodes} />
      <PhoneCard />
      <Card title={t("security.sessionsTitle")} body={t("security.sessionsBody")}>
        <SessionsList />
      </Card>
    </div>
  );
}

/** /account/sessions: the same list on its own page. */
export function SessionsPage() {
  return (
    <div className="flex max-w-3xl flex-col gap-6" data-testid={TID.sessionsPage}>
      <PageHeader title={t("security.sessionsTitle")} lead={t("security.sessionsLead")} />
      <section className="flex flex-col gap-3 rounded-md border border-subtle bg-surface p-5">
        <p className="text-md text-fg-secondary">{t("security.sessionsBody")}</p>
        <SessionsList />
      </section>
    </div>
  );
}
